import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Badge,
  HStack,
  VStack,
  IconButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Alert,
  AlertIcon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from '@chakra-ui/react';
import { FiPlus, FiMoreVertical, FiSearch, FiUsers, FiMic, FiMicOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useVoiceService, VoiceRoom } from '../../hooks/useVoiceService';
import { useVoiceStatusStream } from '../../hooks/useVoiceStatusStream';
import { CreateRoomModal } from './components/CreateRoomModal';

const getRoomTypeLabel = (type: number) => {
  switch (type) {
    case 1: return 'Standard';
    case 2: return 'Workspace';
    case 3: return 'Event';
    case 4: return 'Gaming';
    default: return 'Unknown';
  }
};

const getRoomStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Active';
    case 2: return 'Inactive';
    case 3: return 'Archived';
    case 4: return 'Locked';
    default: return 'Unknown';
  }
};

const getRoomStatusColor = (status: number) => {
  switch (status) {
    case 1: return 'green';
    case 2: return 'gray';
    case 3: return 'orange';
    case 4: return 'red';
    default: return 'gray';
  }
};

export const VoiceRoomsPage: React.FC = () => {
  const [rooms, setRooms] = useState<VoiceRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { getRooms, deleteRoom } = useVoiceService();

  // Get room IDs for status streaming
  const roomIds = rooms.map(room => room.id);
  const {
    roomStatusUpdates,
    initialRoomsData,
    hasInitialData,
    isConnected,
    getLatestRoomStatus
  } = useVoiceStatusStream({
    roomIds: roomIds.length > 0 ? roomIds : [],
    includeInitialState: true,
    enabled: roomIds.length > 0,
  });

  const loadRooms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getRooms();
      setRooms(response.rooms || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [getRooms]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  // Use initial data from WebSocket if available, fallback to REST API
  useEffect(() => {
    if (hasInitialData && initialRoomsData.length > 0) {
      setRooms(initialRoomsData);
      setLoading(false);
    }
  }, [hasInitialData, initialRoomsData]);

  // Update room statuses based on real-time updates
  useEffect(() => {
    if (roomStatusUpdates.length === 0) return;

    setRooms(prevRooms => {
      const updatedRooms = [...prevRooms];
      
      roomStatusUpdates.forEach(update => {
        const roomIndex = updatedRooms.findIndex(room => room.id === update.room_id);
        if (roomIndex !== -1) {
          updatedRooms[roomIndex] = {
            ...updatedRooms[roomIndex],
            ...update.room,
            status: update.status,
          };
        }
      });
      
      return updatedRooms;
    });
  }, [roomStatusUpdates]);

  const handleDeleteRoom = async (roomId: string) => {
    try {
      await deleteRoom(roomId);
      await loadRooms();
    } catch (err) {
      console.error('Failed to delete room:', err);
    }
  };

  const handleRoomCreated = () => {
    onClose();
    loadRooms();
  };

  // Update left sidebar with room actions
  useEffect(() => {
    window.updateLeftSidebar?.([
      {
        label: 'All Rooms',
        path: '/voice/rooms',
        active: true,
      },
      {
        label: 'My Rooms',
        path: '/voice/my-rooms',
      },
    ]);

    return () => window.updateLeftSidebar?.([]);
  }, []);

  // Update right sidebar with room creation and connection status
  useEffect(() => {
    window.updateRightSidebar?.(
      <VStack spacing={4}>
        <Button 
          leftIcon={<FiPlus />} 
          colorScheme="blue" 
          onClick={onOpen}
          width="full"
        >
          Create Room
        </Button>
        
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Connection Status
          </Text>
          <Badge colorScheme={isConnected ? 'green' : 'red'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </Box>

        {rooms.length > 0 && (
          <VStack align="stretch" spacing={2} width="full">
            <Text fontSize="sm" fontWeight="medium">
              Live Updates
            </Text>
            
            <VStack align="stretch" spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Total Rooms: {rooms.length}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Active Streams: {roomIds.length}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Updates: {roomStatusUpdates.length}
              </Text>
            </VStack>
          </VStack>
        )}
      </VStack>
    );

    return () => window.updateRightSidebar?.(null);
  }, [onOpen, isConnected, rooms.length, roomIds.length, roomStatusUpdates.length]);

  // Filter rooms based on search and selection
  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || room.type.toString() === selectedType;
    const matchesStatus = !selectedStatus || room.status.toString() === selectedStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={6} />
        <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height="200px" />
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Voice Rooms</Heading>
        <HStack>
          <Text fontSize="sm" color="gray.500">
            {filteredRooms.length} room{filteredRooms.length !== 1 ? 's' : ''}
          </Text>
          {isConnected && (
            <Badge colorScheme="green" variant="subtle">
              Live Updates
            </Badge>
          )}
        </HStack>
      </HStack>

      {filteredRooms.length === 0 ? (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <FiMic size={48} color="gray" />
              <Text color="gray.500">
                {rooms.length === 0 ? 'No voice rooms yet' : 'No rooms match your filters'}
              </Text>
              <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
                Create First Room
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={6}>
          {filteredRooms.map((room) => {
            const latestStatus = getLatestRoomStatus(room.id);
            const currentStatus = latestStatus?.status ?? room.status;
            
            return (
              <Card 
                key={room.id} 
                cursor="pointer"
                _hover={{ shadow: 'md' }}
                onClick={() => navigate(`/voice/rooms/${room.id}`)}
              >
                <CardHeader pb={2}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontWeight="bold" noOfLines={1}>
                        {room.name}
                      </Text>
                      <HStack>
                        <Badge colorScheme="blue" variant="subtle">
                          {getRoomTypeLabel(room.type)}
                        </Badge>
                        <Badge colorScheme={getRoomStatusColor(currentStatus)} variant="subtle">
                          {getRoomStatusLabel(currentStatus)}
                        </Badge>
                      </HStack>
                    </VStack>
                    
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList>
                        <MenuItem onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/voice/rooms/${room.id}/edit`);
                        }}>
                          Edit Room
                        </MenuItem>
                        <MenuItem onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/voice/rooms/${room.id}/settings`);
                        }}>
                          Settings
                        </MenuItem>
                        <MenuItem 
                          color="red.500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRoom(room.id);
                          }}
                        >
                          Delete Room
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </CardHeader>
                
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="sm" color="gray.600" noOfLines={2}>
                      {room.description}
                    </Text>
                    
                    <HStack justify="space-between">
                      <HStack>
                        <FiUsers />
                        <Text fontSize="sm">
                          {room.groups_count} group{room.groups_count !== 1 ? 's' : ''}
                        </Text>
                      </HStack>
                      
                      <HStack>
                        {room.settings.is_public ? <FiMic /> : <FiMicOff />}
                        <Text fontSize="sm">
                          {room.settings.is_public ? 'Public' : 'Private'}
                        </Text>
                      </HStack>
                    </HStack>
                    
                    {latestStatus && (
                      <Text fontSize="xs" color="blue.500">
                        Last updated: {new Date(latestStatus.timestamp).toLocaleTimeString()}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      <CreateRoomModal
        isOpen={isOpen}
        onClose={onClose}
        onRoomCreated={handleRoomCreated}
      />
    </Box>
  );
};